import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "./auth";
import { LOW_TRUST_ROLES } from "./modules";
import { useSchool } from "./school";
import api, { API } from "./api";

const ChatContext = createContext(null);
const notifSupported = typeof window !== "undefined" && "Notification" in window;
const ACT_SEEN_KEY = "omni_activity_seen_at";

// Single app-wide realtime chat connection (presence, typing, read receipts, unread, @mention alerts).
export function ChatProvider({ children }) {
  const { user } = useAuth();
  const { activeSchoolId } = useSchool();
  const [connected, setConnected] = useState(false);
  const [online, setOnline] = useState(() => new Set());
  const [unread, setUnread] = useState({}); // channel_id -> count
  const [activities, setActivities] = useState([]); // collaboration activity feed
  const [activitySeenAt, setActivitySeenAt] = useState(() => localStorage.getItem(ACT_SEEN_KEY) || "");
  const [notifPermission, setNotifPermission] = useState(notifSupported ? Notification.permission : "unsupported");

  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const reconnectRef = useRef(null);
  const activeChannelRef = useRef(null);

  const emit = useCallback((evt) => {
    listenersRef.current.forEach((fn) => { try { fn(evt); } catch { /* ignore */ } });
  }, []);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const setActiveChannel = useCallback((id) => { activeChannelRef.current = id; }, []);

  const loadUnread = useCallback(async () => {
    try { const { data } = await api.get("/chat/unread"); setUnread(data || {}); } catch { /* ignore */ }
  }, []);

  const lowTrust = LOW_TRUST_ROLES.includes(user?.role);
  const loadActivity = useCallback(async () => {
    if (lowTrust) return;
    try { const { data } = await api.get("/activity?limit=50"); setActivities(data || []); } catch { /* ignore */ }
  }, [lowTrust]);

  const markActivityRead = useCallback(() => {
    const ts = new Date().toISOString();
    setActivitySeenAt(ts);
    try { localStorage.setItem(ACT_SEEN_KEY, ts); } catch { /* ignore */ }
  }, []);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) { ws.send(JSON.stringify(payload)); return true; }
    return false;
  }, []);

  const clearUnread = useCallback((channelId) => {
    setUnread((prev) => { if (!prev[channelId]) return prev; const n = { ...prev }; delete n[channelId]; return n; });
  }, []);

  const enableNotifications = useCallback(async () => {
    if (!notifSupported) return "unsupported";
    try { const p = await Notification.requestPermission(); setNotifPermission(p); return p; } catch { return "denied"; }
  }, []);

  useEffect(() => {
    if (!user || !activeSchoolId) return undefined;
    let stopped = false;
    loadUnread();
    loadActivity();

    const notifyMention = (m) => {
      toast(`${m.sender_name} mentioned you`, {
        description: m.body || "",
        action: { label: "Open", onClick: () => { window.location.assign("/app/messages"); } },
      });
      try {
        if (notifSupported && Notification.permission === "granted") {
          const n = new Notification(`${m.sender_name} mentioned you`, { body: m.body || "", tag: m.channel_id });
          n.onclick = () => { window.focus(); window.location.assign("/app/messages"); };
        }
      } catch { /* ignore */ }
    };

    const handle = (data) => {
      switch (data.type) {
        case "presence_snapshot":
          setOnline(new Set(data.online || []));
          break;
        case "presence":
          setOnline((prev) => {
            const n = new Set(prev);
            if (data.online) n.add(data.user_id); else n.delete(data.user_id);
            return n;
          });
          break;
        case "message": {
          const m = data.message;
          if (m.sender_id !== user.id && m.channel_id !== activeChannelRef.current) {
            setUnread((prev) => ({ ...prev, [m.channel_id]: (prev[m.channel_id] || 0) + 1 }));
          }
          if (m.sender_id !== user.id && Array.isArray(m.mentions) && m.mentions.includes(user.id)) {
            notifyMention(m);
          }
          emit(data);
          break;
        }
        case "read":
        case "typing":
          emit(data);
          break;
        case "activity": {
          const act = data.activity;
          if (act && act.actor_id !== user.id) {
            queueMicrotask(() => {
              setActivities((prev) => [act, ...prev.filter((p) => p.id !== act.id)].slice(0, 50));
              toast(act.summary, { description: act.sport || undefined });
            });
          }
          emit(data);
          break;
        }
        default:
          break;
      }
    };

    const connect = () => {
      if (stopped) return;
      const token = localStorage.getItem("omni_token");
      if (!token) return;
      const wsBase = API.replace(/^http/, "ws"); // https://host/api -> wss://host/api
      const url = `${wsBase}/ws/chat?token=${encodeURIComponent(token)}&school=${encodeURIComponent(activeSchoolId)}`;
      let ws;
      try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => { if (!stopped) setConnected(true); };
      ws.onmessage = (e) => { let d; try { d = JSON.parse(e.data); } catch { return; } handle(d); };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      ws.onclose = () => {
        if (stopped) return;
        setConnected(false);
        reconnectRef.current = setTimeout(connect, 2500);
      };
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      const ws = wsRef.current;
      if (ws) { ws.onclose = null; try { ws.close(); } catch { /* ignore */ } }
      wsRef.current = null;
      setConnected(false);
      setOnline(new Set());
    };
  }, [user?.id, activeSchoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalUnread = Object.values(unread).reduce((a, b) => a + (b || 0), 0);
  const seenMs = activitySeenAt ? new Date(activitySeenAt).getTime() : 0;
  const activityUnread = activities.filter((a) => a.actor_id !== user?.id && new Date(a.created_at).getTime() > seenMs).length;

  return (
    <ChatContext.Provider value={{ connected, online, unread, totalUnread, send, subscribe, setActiveChannel, clearUnread, loadUnread, notifPermission, enableNotifications, notifSupported, activities, activityUnread, markActivityRead, loadActivity }}>
      {children}
    </ChatContext.Provider>
  );
}
export const useChat = () => useContext(ChatContext);
